/**
 * Tools that add, edit, remove and read an object's behaviors, one or a batch
 * at a time, and list the behavior types there are. Text a call writes into a
 * Text behavior arrives double-escaped from a model and is normalized here
 * (`normalizeAuthoredText`).
 */

import type { SceneObject } from '../../core/SceneObject';
import type { TraceBehavior } from '../../behaviors/tracebehavior';
import { getBehaviorConstructor, type BehaviorConstructor } from '../../behaviors/BehaviorRegistry';
import { BehaviorGraph } from '../../behaviors/behaviorgraph/behaviorgraph';
import { raiseBehaviorGraphData } from '../../behaviors/behaviorgraph/macros';
import { Renderer } from '../../behaviors/renderer';
import { getAddableBehaviorTypes, getBehaviorSchema } from '../../scenedata/schemas/index';
import { formatBehaviorSchema } from '../../scenedata/schemas/format';
import { normalizeAuthoredText } from '../authoredText';
import { addBehaviors, commitBehaviors, removeBehaviors, type BehaviorBatchResult } from '../authoring/behaviorOps';
import { bundle, rollBack } from '../authoring/steps';
import { dispatchBehaviorChanged } from '../undo/editor-events';
import type { UndoCommand } from '../undo/UndoCommand';
import { ObjectRefError, defineTool, fail, keep, refusalText, resolveRef, type ToolDefinition, type ToolInput } from './input';
import type { SceneTool, SceneToolHost, ToolOutcome } from './types';

/** `message`, then each thing fitting the input to the format dropped. */
function withWarnings(message: string, warnings: readonly { message: string }[]): string {
    return warnings.length > 0 ? `${message}\n⚠️ ${warnings.map(w => w.message).join('\n⚠️ ')}` : message;
}

/** Text data with real newlines: tool input arrives double-escaped, and the runtime renders strings verbatim. */
function normalizedText(type: string, data: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
    return type === 'Text' && typeof data?.text === 'string' ? { ...data, text: normalizeAuthoredText(data.text) } : data;
}

const isData = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value);

/** Why `field` is not an object of fields, which a model may send as a JSON string; null when it is one, or absent (or null) and `optional`. */
function dataError(field: string, value: unknown, optional: boolean): ToolOutcome | null {
    if (isData(value) || (optional && (value === undefined || value === null))) return null;
    return fail(`Error: ${field} must be an object of fields, not ${JSON.stringify(value)?.slice(0, 80) ?? String(value)}. Nothing changed.`);
}

/** Longest behavior data a write echoes back in full. */
const ECHO_DATA_CHARS = 1000;

/**
 * What a write reports about the data now stored: all of it when short, so
 * normalized values show; otherwise only the fields written (a deck's `pages`
 * can run to thousands of entries).
 */
function dataEcho(json: string, written: string[]): string {
    if (json.length <= ECHO_DATA_CHARS) return ` Current data:\n${json}`;
    const fields = written.filter(k => k !== '@type');
    return ` Wrote ${fields.join(', ') || '(no fields)'}; the data is ${json.length} characters, so it is not echoed — read it with get_behavior if needed.`;
}

/** slotIndex → live material name for a Renderer: the mesh's material, else the original material's. */
function rendererSlotNames(renderer: Renderer): Map<number, string> {
    const names = new Map<number, string>();
    for (const [slot, mesh] of renderer.getMeshSlots()) {
        const name = mesh.material?.name;
        if (name) names.set(slot, name);
    }
    for (const [slot, name] of Object.entries(renderer.getOriginalMaterials())) {
        if (name && !names.has(Number(slot))) names.set(Number(slot), name);
    }
    return names;
}

/**
 * A behavior's data as a model reads it. A BehaviorGraph reads raised, each
 * macro expansion as the one `macro/…` block it was authored as, which is the
 * form a commit takes back; `expanded` gives the stored nodes and the runtime
 * diagnostics. A Renderer's material overrides carry their live material
 * names, and the slots are listed, so a slot is chosen by name.
 */
export function behaviorDataText(behavior: TraceBehavior, expanded = false): string {
    if (behavior instanceof BehaviorGraph && !expanded) {
        return JSON.stringify(raiseBehaviorGraphData(behavior.getData()), null, 2);
    }
    const data = behavior.getData() as unknown as Record<string, unknown>;
    if (behavior instanceof BehaviorGraph) {
        return JSON.stringify({ ...data, runtimeDiagnostics: behavior.getRuntimeDiagnostics() }, null, 2);
    }
    if (behavior instanceof Renderer) {
        const slotNames = rendererSlotNames(behavior);
        if (Array.isArray(data.materials)) {
            for (const material of data.materials as unknown[]) {
                if (!material || typeof material !== 'object') continue;
                const name = slotNames.get((material as { targetMaterialIndex?: number }).targetMaterialIndex ?? 0);
                if (name) (material as Record<string, unknown>).material = name;
            }
        }
        if (slotNames.size > 0) {
            data.materialSlots = [...slotNames.entries()].sort((a, b) => a[0] - b[0]).map(([index, material]) => ({ index, material }));
        }
    }
    return JSON.stringify(data, null, 2);
}

/** The behavior of type `type` on the object `ref` names, or the answer saying why there is none. */
function findBehavior(host: SceneToolHost, ref: unknown, type: unknown): TraceBehavior | ToolOutcome {
    const target = resolveRef(host, ref);
    if (!target) return fail(`Error: Object "${String(ref)}" not found.`);
    // By its type name: a custom script has no class of its own.
    const behavior = typeof type === 'string' ? target.getBehavior(type) : null;
    if (behavior) return behavior;
    const available = target.getBehaviors().map(b => b.name);
    return fail(`Error: No "${String(type)}" behavior on "${target.name}". Behaviors: ${available.join(', ') || '(none)'}`);
}

const isOutcome = (value: object): value is ToolOutcome => 'text' in value;

const OBJECT_AND_TYPE = { object: { type: 'string' }, behavior_type: { type: 'string' } } as const;
const ADD_FIELDS = { ...OBJECT_AND_TYPE, overrides: { type: 'object' } } as const;
const COMMIT_FIELDS = { ...OBJECT_AND_TYPE, data: { type: 'object' } } as const;

interface AddEntry { target: SceneObject; Behavior: BehaviorConstructor; type: string; overrides: Record<string, unknown> | undefined }
interface CommitEntry { behavior: TraceBehavior; type: string; data: Record<string, unknown> }

/** An add_behavior call's input, resolved, or the answer saying why it is not one. */
function readAdd(input: ToolInput, host: SceneToolHost): AddEntry | ToolOutcome {
    const type = String(input.behavior_type);
    const invalid = dataError('overrides', input.overrides, true);
    if (invalid) return invalid;
    const target = resolveRef(host, input.object);
    if (!target) return fail(`Error: Object "${String(input.object)}" not found.`);
    const Behavior = getBehaviorConstructor(type);
    if (!Behavior) {
        return fail(`Error: Unknown behavior type "${type}". Available types: ${getAddableBehaviorTypes().join(', ')}. Use list_behaviors to see schemas.`);
    }
    return { target, Behavior, type, overrides: normalizedText(type, isData(input.overrides) ? input.overrides : undefined) };
}

/**
 * Add the behavior, or commit the overrides to the one of its type the object
 * has; null when it has one and there is nothing to write. Throws on a fault.
 */
async function writeAdd({ target, Behavior, overrides }: AddEntry, host: SceneToolHost): Promise<{ result: BehaviorBatchResult | null; existing: TraceBehavior | null }> {
    const existing = target.getBehavior(Behavior);
    if (!existing) return { result: await addBehaviors([{ object: target, type: Behavior, data: overrides }], host.ports()), existing };
    if (!overrides || Object.keys(overrides).length === 0) return { result: null, existing };
    return { result: await commitBehaviors([{ behavior: existing, patch: overrides }], host.ports()), existing };
}

/** A commit_behavior call's input, resolved, or the answer saying why it is not one. */
function readCommit(input: ToolInput, host: SceneToolHost): CommitEntry | ToolOutcome {
    const behavior = findBehavior(host, input.object, input.behavior_type);
    if (isOutcome(behavior)) return behavior;
    const type = String(input.behavior_type);
    const invalid = dataError('data', input.data, false);
    if (invalid) return invalid;
    return { behavior, type, data: normalizedText(type, input.data as Record<string, unknown>) ?? {} };
}

/** Commit the patch; the model's @type is replaced, not trusted. Throws on a fault. */
function writeCommit({ behavior, type, data }: CommitEntry, host: SceneToolHost): Promise<BehaviorBatchResult> {
    return commitBehaviors([{ behavior, patch: { ...data, '@type': type } }], host.ports());
}

/** The call's `entries`; null for a call on one object. An answer when it gives both `entries` and `object`. */
function entryList(input: ToolInput, tool: string): { list: unknown } | null | ToolOutcome {
    if (input.entries === undefined) return null;
    return input.object !== undefined ? fail(`Error: ${tool} takes \`object\` or \`entries\`, not both. Nothing changed.`) : { list: input.entries };
}

const ADD_BEHAVIOR: ToolDefinition = {
    name: 'add_behavior',
    verb: 'Adding a behavior to',
    edits: true,
    serial: true,
    inputSchema: { type: 'object', properties: { ...ADD_FIELDS, entries: { type: 'array', items: { type: 'object', properties: ADD_FIELDS, required: ['object', 'behavior_type'] } } } },
    async run(input, host) {
        const many = entryList(input, 'add_behavior');
        if (many) return isOutcome(many) ? many : behaviorBatch('add_behavior', many.list, host, readAdd, writeAddEntry);
        const entry = readAdd(input, host);
        if (isOutcome(entry)) return entry;
        const { target, type, overrides } = entry;
        const had = !!target.getBehavior(entry.Behavior);
        let written: Awaited<ReturnType<typeof writeAdd>>;
        try {
            written = await writeAdd(entry, host);
        } catch (error) {
            return fail(`Error ${had ? 'updating' : 'adding'} behavior: ${error instanceof Error ? error.message : String(error)}`);
        }
        const { result, existing } = written;
        if (!result) return { text: `"${target.name}" already has a "${type}" behavior (no changes needed).` };
        const [refused] = result.refused;
        if (refused) return fail(`Error: ${refusalText(host, refused)}`);
        keep(host, 'add_behavior', result);
        // Adding one that is there already updated it.
        return { text: withWarnings(existing
            ? `Updated "${type}" on "${target.name}" (behavior already existed).${dataEcho(JSON.stringify(existing.getData()), Object.keys(overrides ?? {}))}`
            : `Added "${type}" to "${target.name}".`, result.warnings) };
    },
};
const addBehaviorTool = defineTool(ADD_BEHAVIOR);

const COMMIT_BEHAVIOR: ToolDefinition = {
    name: 'commit_behavior',
    verb: 'Updating a behavior on',
    edits: true,
    idempotent: true,
    serial: true,
    inputSchema: { type: 'object', properties: { ...COMMIT_FIELDS, entries: { type: 'array', items: { type: 'object', properties: COMMIT_FIELDS, required: ['object', 'behavior_type', 'data'] } } } },
    async run(input, host) {
        const many = entryList(input, 'commit_behavior');
        if (many) return isOutcome(many) ? many : behaviorBatch('commit_behavior', many.list, host, readCommit, writeCommitEntry);
        const entry = readCommit(input, host);
        if (isOutcome(entry)) return entry;
        const { behavior, type, data } = entry;
        const before = JSON.stringify(behavior.getData());
        let result: BehaviorBatchResult;
        try {
            result = await writeCommit(entry, host);
        } catch (error) {
            return fail(`Error committing behavior: ${error instanceof Error ? error.message : String(error)}`);
        }
        const [refused] = result.refused;
        if (refused) return fail(`Error: ${refusalText(host, refused)}`);
        keep(host, 'commit_behavior', result);
        const after = JSON.stringify(behavior.getData());
        const name = behavior.sceneObject.name;
        if (before === after) {
            return fail(result.warnings.length > 0
                ? withWarnings(`Warning: commit to "${type}" on "${name}" had no effect — all data was stripped or invalid.`, result.warnings)
                : `No changes: "${type}" on "${name}" already has this data. Do not retry.`);
        }
        return { text: withWarnings(`Updated "${type}" on "${name}".${dataEcho(after, Object.keys(data))}`, result.warnings) };
    },
};
const commitBehaviorTool = defineTool(COMMIT_BEHAVIOR);

/** What a batch entry wrote: the operation's result (null when there was nothing to write), whether it added the behavior, and its answer line. */
interface Written { result: BehaviorBatchResult | null; added: boolean; line: string }

/**
 * A call over several behaviors, all or nothing: every entry is read before
 * any is written, and the first write refused or faulting takes back the ones
 * before it; either way the answer names the entry by index. The writes stand
 * as one undo step, answered one line per entry in order.
 */
async function behaviorBatch<E extends object>(
    tool: string,
    updates: unknown,
    host: SceneToolHost,
    read: (fields: ToolInput, host: SceneToolHost) => E | ToolOutcome,
    write: (entry: E, host: SceneToolHost) => Promise<Written>,
): Promise<ToolOutcome> {
    if (!Array.isArray(updates) || updates.length === 0) return fail(`Error: Provide \`entries\` as a non-empty array of entries.`);
    const entries: E[] = [];
    const errors: string[] = [];
    for (const [index, update] of (updates as unknown[]).entries()) {
        let entry: E | ToolOutcome;
        try {
            entry = read(isData(update) ? update : {}, host);
        } catch (error) {
            if (!(error instanceof ObjectRefError)) throw error;
            entry = fail(error.message);
        }
        if (isOutcome(entry)) errors.push(`  entries[${index}]: ${entry.text.replace(/^Error: /, '')}`);
        else entries.push(entry);
    }
    if (errors.length > 0) return fail(`${tool}: nothing changed. Fix these entries and send the whole call again:\n${errors.join('\n')}`);

    const steps: UndoCommand[] = [];
    // What the operations announced, to correct if this takes them back: that undo is silent.
    const told: { objectPath: string; behaviorType: string; action: 'removed' | 'updated' }[] = [];
    const orphaned: string[] = [];
    const lines: string[] = [];
    for (const [index, entry] of entries.entries()) {
        let written: Written | null = null;
        let problem: string | null = null;
        try {
            written = await write(entry, host);
            const [refused] = written.result?.refused ?? [];
            if (refused) problem = refusalText(host, refused);
        } catch (error) {
            problem = error instanceof Error ? error.message : String(error);
        }
        if (problem !== null || !written) {
            const why = (problem ?? 'it wrote nothing').replace(/([^.!?])$/, '$1.');
            return fail(`${tool}: entries[${index}]: ${why} ${await takeBack(steps, told, why)}`);
        }
        const { result, added } = written;
        if (result?.command) {
            steps.push(result.command);
            told.push(...result.applied.map(b => ({ objectPath: b.sceneObject.getPath(), behaviorType: b.name, action: added ? 'removed' as const : 'updated' as const })));
        }
        orphaned.push(...(result?.orphaned ?? []));
        lines.push(`  entries[${index}]: ${withWarnings(written.line, written.result?.warnings ?? [])}`);
    }
    keep(host, tool, { command: steps.length > 0 ? bundle(`${tool} (${entries.length})`, steps) : null, orphaned });
    return { text: `${tool}: ${steps.length > 0 ? `${entries.length} applied, one undo step` : 'nothing to change'}.\n${lines.join('\n')}` };
}

/** Take back a batch's earlier steps and correct what they announced; says whether anything is left. */
async function takeBack(steps: readonly UndoCommand[], told: readonly Parameters<typeof dispatchBehaviorChanged>[0][], cause: string): Promise<string> {
    try {
        await rollBack(steps, new Error(cause));
        return 'Nothing in this call was applied.';
    } catch (error) {
        const failures = error instanceof AggregateError ? error.errors.slice(1) : [error];
        return `Taking back the entries before it failed too (${failures.map(f => f instanceof Error ? f.message : String(f)).join('; ')}), so some may still be applied: check them with get_behavior.`;
    } finally {
        // Newest first, so a behavior added and then updated in the call ends on its removal.
        for (const event of [...told].reverse()) dispatchBehaviorChanged(event);
    }
}

/** One add_behavior entry of several, written, with its line. */
async function writeAddEntry(entry: AddEntry, host: SceneToolHost): Promise<Written> {
    const { result, existing } = await writeAdd(entry, host);
    const what = `"${entry.type}" on "${entry.target.name}"`;
    return { result, added: !existing, line: !existing ? `added ${what}` : result ? `updated ${what} (it had one)` : `${what} already there` };
}

/** One commit_behavior entry of several, written, with its line. */
async function writeCommitEntry(entry: CommitEntry, host: SceneToolHost): Promise<Written> {
    const before = JSON.stringify(entry.behavior.getData());
    const result = await writeCommit(entry, host);
    const changed = JSON.stringify(entry.behavior.getData()) !== before;
    return { result, added: false, line: `${changed ? 'updated' : 'no change to'} "${entry.type}" on "${entry.behavior.sceneObject.name}"` };
}

const removeBehaviorTool = defineTool({
    name: 'remove_behavior',
    verb: 'Removing a behavior from',
    edits: true,
    idempotent: true,
    serial: true,
    inputSchema: { type: 'object', properties: OBJECT_AND_TYPE, required: ['object', 'behavior_type'] },
    async run(input, host) {
        const behavior = findBehavior(host, input.object, input.behavior_type);
        if (isOutcome(behavior)) return behavior;
        const name = behavior.sceneObject.name;
        let result: Awaited<ReturnType<typeof removeBehaviors>>;
        try {
            result = await removeBehaviors([behavior], host.ports());
        } catch (error) {
            return fail(`Error removing behavior: ${error instanceof Error ? error.message : String(error)}`);
        }
        const [refused] = result.refused;
        if (refused) return fail(`Error: ${refusalText(host, refused)}`);
        keep(host, 'remove_behavior', result);
        return { text: `Removed "${String(input.behavior_type)}" from "${name}".` };
    },
});

const getBehaviorTool = defineTool({
    name: 'get_behavior',
    verb: 'Reading',
    edits: false,
    inputSchema: { type: 'object', properties: { ...OBJECT_AND_TYPE, expanded: { type: 'boolean' } }, required: ['object', 'behavior_type'] },
    run(input, host) {
        const behavior = findBehavior(host, input.object, input.behavior_type);
        return Promise.resolve(isOutcome(behavior) ? behavior : { text: behaviorDataText(behavior, input.expanded === true) });
    },
});

/** Longest one-line blurb the type summary carries, ellipsis included. */
const SUMMARY_DESCRIPTION_CHARS = 100;

/** A description's first sentence, on one line and capped. Abbreviation dots ("e.g.") end no sentence. */
export function summarizeDescription(description: string, limit = SUMMARY_DESCRIPTION_CHARS): string {
    const flat = description.replace(/\s+/g, ' ').trim();
    let sentence = flat;
    for (const match of flat.matchAll(/[.!?](?=\s|$)/g)) {
        const index = match.index;
        if (flat[index] === '.' && /\w\.\w$/.test(flat.slice(Math.max(0, index - 3), index))) continue;
        sentence = flat.slice(0, index + 1);
        break;
    }
    return sentence.length > limit ? sentence.slice(0, limit - 1).trimEnd() + '…' : sentence;
}

const listBehaviorsTool = defineTool({
    name: 'list_behaviors',
    verb: 'Listing behaviors',
    edits: false,
    inputSchema: { type: 'object', properties: { behavior_type: { type: 'string' } } },
    run(input) {
        const type = typeof input.behavior_type === 'string' && input.behavior_type ? input.behavior_type : null;
        if (type) {
            const schema = getBehaviorSchema(type);
            return Promise.resolve(schema
                ? { text: formatBehaviorSchema(schema) }
                : fail(`Unknown behavior type "${type}". Available types: ${getAddableBehaviorTypes().join(', ')}`));
        }
        // One line a type: the full descriptions run past what a result can carry; a named type gets them.
        const lines = ['Available behavior types:\n'];
        for (const name of getAddableBehaviorTypes()) {
            const schema = getBehaviorSchema(name);
            const description = schema?.description ? ` — ${summarizeDescription(schema.description)}` : '';
            const fields = schema?.fields.filter(f => !f.hidden).length ?? 0;
            lines.push(`  ${name}${description} (${fields} fields)${schema?.docs ? ' [has docs]' : ''}`);
        }
        lines.push('\nUse list_behaviors with a specific type to see its full schema.');
        return Promise.resolve({ text: lines.join('\n') });
    },
});

export const BEHAVIOR_TOOLS: readonly SceneTool[] = [addBehaviorTool, commitBehaviorTool, removeBehaviorTool, getBehaviorTool, listBehaviorsTool];
